import type * as React from 'react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { type NormalizationMode, hasInvalidIdentifierChars, normalizeIdentifier } from '@/utils/normalization'

export interface InputProps extends React.ComponentProps<'input'> {
	normalize?: boolean | NormalizationMode
}

function Input({ className, type, normalize, onChange, onBlur, ...props }: InputProps) {
	// Support both controlled and uncontrolled states
	const [localValue, setLocalValue] = useState<string>(
		String((props.value !== undefined ? props.value : props.defaultValue) ?? ''),
	)

	// Keep local state in sync with external value updates
	useEffect(() => {
		if (props.value !== undefined) {
			setLocalValue(String(props.value))
		}
	}, [props.value])

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (normalize) {
			const originalValue = e.target.value
			const normalized = normalizeIdentifier(originalValue, normalize)
			setLocalValue(normalized)

			if (originalValue !== normalized) {
				const start = e.target.selectionStart
				e.target.value = normalized

				if (start !== null) {
					// Safely restore cursor position after react updates
					const diff = originalValue.length - normalized.length
					const newCursor = Math.max(0, start - diff)
					requestAnimationFrame(() => {
						e.target.setSelectionRange(newCursor, newCursor)
					})
				}
			}
		} else {
			setLocalValue(e.target.value)
		}
		onChange?.(e)
	}

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		if (normalize) {
			const originalValue = e.target.value
			const normalized = normalizeIdentifier(originalValue, normalize)
			setLocalValue(normalized)

			if (originalValue !== normalized) {
				e.target.value = normalized
				// Notify React/RHF of the change
				onChange?.(e as unknown as React.ChangeEvent<HTMLInputElement>)
			}
		}
		onBlur?.(e)
	}

	const hasSpecialChars = hasInvalidIdentifierChars(localValue, normalize)

	return (
		<input
			type={type}
			data-slot='input'
			className={cn(
				'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-(--height-control) w-full min-w-0 rounded-md border bg-card px-3 py-1 text-body outline-none file:inline-flex file:h-control file:border-0 file:bg-transparent file:text-body file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
				'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[1px]',
				'aria-invalid-style',
				className,
			)}
			onChange={handleChange}
			onBlur={handleBlur}
			aria-invalid={hasSpecialChars ? 'true' : props['aria-invalid']}
			{...props}
		/>
	)
}

export { Input }
